<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue'
import Chat from '../Chat.vue'

const props = defineProps({
  question: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['proceed'])

// Track selected options
const selectedOptions = ref([])

// Check if we can proceed (at least one selection)
const canProceed = computed(() => selectedOptions.value.length > 0)

function toggleOption(value) {
  const index = selectedOptions.value.indexOf(value)
  if (index > -1) {
    selectedOptions.value.splice(index, 1)
  } else {
    selectedOptions.value.push(value)
  }

  // Update question value
  props.question.value = [...selectedOptions.value]
}

function isSelected(value) {
  return selectedOptions.value.includes(value)
}

function handleContinue() {
  if (canProceed.value) {
    emit('proceed', selectedOptions.value)
  }
}
</script>

<template>
  <section class="multi-select">
    <h3 class="questionHeading">{{ question.headline }}</h3>
    <p v-if="question.subheadline" class="subheadline">{{ question.subheadline }}</p>

    <div
      class="checkboxBtn"
      v-for="option in question.options"
      :key="question.id + '-' + option.value"
      :class="{ selected: isSelected(option.value) }"
    >
      <input
        type="checkbox"
        :id="question.id + '-' + option.value"
        :value="option.value"
        :checked="isSelected(option.value)"
        @change="toggleOption(option.value)"
      />
      <label :for="question.id + '-' + option.value">
        <span class="checkbox-icon">
          <i v-if="isSelected(option.value)" class="fas fa-check"></i>
        </span>
        {{ option.text }}
      </label>
    </div>

    <button
      class="continueBtn"
      :disabled="!canProceed"
      @click="handleContinue"
    >
      Continue
    </button>

    <div v-if="question.chat">
      <Chat :message="question.chat" />
    </div>
  </section>
</template>

<style scoped>
.questionHeading {
  font-size: 1.6em;
  font-weight: 700;
  color: #2a2a2b;
  text-align: center;
  line-height: 1.2;
  margin-bottom: 10px;
}

.subheadline {
  text-align: center;
  color: #666;
  margin-bottom: 20px;
  font-size: 0.95em;
}

.checkboxBtn {
  position: relative;
  display: block;
  margin-bottom: 10px;
}

.checkboxBtn label {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  color: #0B3954;
  border-radius: 1px;
  padding: 18px 15px;
  border: 1px solid #0B3954;
  cursor: pointer;
  box-shadow: 0 3px 0 #dbdBE866;
  font-size: 1.1em;
  font-weight: 500;
}

.checkboxBtn.selected label {
  background: #f0f7ff;
  border-color: #0B3954;
  border-width: 2px;
}

.checkbox-icon {
  width: 24px;
  height: 24px;
  border: 2px solid #0B3954;
  border-radius: 4px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.checkboxBtn.selected .checkbox-icon {
  background: #0B3954;
  color: #fff;
}

input[type="checkbox"] {
  display: none;
}

.continueBtn {
  display: block;
  width: 100%;
  background: #0B3954;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 18px 20px;
  font-size: 1.2em;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  transition: opacity 0.2s;
}

.continueBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.continueBtn:not(:disabled):hover {
  opacity: 0.9;
}
</style>
